/** @fileoverview Builds CSS variables from a Fabrik app theme configuration. */

import type { FabrikAppDefinition } from '../../types/apps';
import type { ThemeStyle } from './types';

const colorValue = (value: string | undefined, fallback: string): string =>
  value && CSS.supports('color', value) ? value : fallback;

const linearChannel = (value: number): number => {
  const channel = value / 255;
  return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
};

export const logoForegroundColor = (color: string): string => {
  const match = /^#([\da-f]{6})$/i.exec(color);
  if (!match) return '#ffffff';
  const red = parseInt(match[1].slice(0, 2), 16);
  const green = parseInt(match[1].slice(2, 4), 16);
  const blue = parseInt(match[1].slice(4, 6), 16);
  const luminance =
    0.2126 * linearChannel(red) + 0.7152 * linearChannel(green) + 0.0722 * linearChannel(blue);
  return luminance > 0.179 ? '#111827' : '#ffffff';
};

export const paletteStyle = (theme?: FabrikAppDefinition['theme']): ThemeStyle => {
  const primary = colorValue(theme?.primaryColor, '#42586e');
  const foreground = 'var(--app-text)';
  const surface = 'var(--app-surface-card)';
  const panel = `color-mix(in srgb, ${primary} 7%, ${surface})`;
  const border = 'color-mix(in srgb, var(--app-border) 94%, var(--primary))';

  return {
    '--primary': primary,
    '--primary-foreground': '#ffffff',
    '--secondary': panel,
    '--secondary-foreground': foreground,
    '--background': 'var(--app-surface-page)',
    '--foreground': foreground,
    '--card': surface,
    '--card-foreground': foreground,
    '--popover': surface,
    '--popover-foreground': foreground,
    '--muted': panel,
    '--muted-foreground': `color-mix(in srgb, ${foreground} 65%, ${surface})`,
    '--border': border,
    '--input': border,
    '--ring': primary,
  };
};
